/**
 * Ticket attachments in the agent console (owner 2026-10-14): show the user's photo / video / PDF and
 * let the agent attach a file to the reply. Every view is audited by the API.
 */
import { FileText, Loader2, Paperclip } from 'lucide-react';
import { useRef, useState } from 'react';

export interface Attachment {
  id: string;
  messageId: string;
  contentType: string;
  byteSize: number;
  fileName: string | null;
}

const BASE = '/api/v1/admin/support/attachments';
const ACCEPT = 'image/jpeg,image/png,application/pdf,video/mp4,video/webm,video/quicktime';
const kb = (n: number) =>
  n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

export function AgentAttachment({ a }: { a: Attachment }) {
  const src = `${BASE}/${a.id}`;
  const label = a.fileName ?? 'پیوست';
  if (a.contentType.startsWith('image/'))
    return (
      <a href={src} target="_blank" rel="noopener" className="block w-fit" data-attachment="image">
        <img
          src={src}
          alt={label}
          loading="lazy"
          className="max-h-64 max-w-full rounded-md border border-line object-contain"
        />
      </a>
    );
  if (a.contentType.startsWith('video/'))
    return (
      <video
        src={`${src}#t=0.001`}
        controls
        playsInline
        preload="metadata"
        className="max-h-72 w-full max-w-md rounded-md border border-line bg-black"
        aria-label={label}
        data-attachment="video"
      />
    );
  return (
    <a
      href={src}
      download={a.fileName ?? 'attachment.pdf'}
      className="inline-flex w-fit items-center gap-2 rounded-md border border-line bg-surface-1 px-3 py-2 text-[13px] font-semibold hover:border-accent-soft"
      data-attachment="pdf"
    >
      <FileText aria-hidden className="size-5 text-negative" />
      <span className="max-w-[18rem] truncate">{label}</span>
      <span className="text-[11.5px] font-normal text-fg-subtle" dir="ltr">
        {kb(a.byteSize)}
      </span>
    </a>
  );
}

export function AgentAttachButton({
  threadId,
  onDone,
  onError,
}: {
  threadId: string;
  onDone: () => void;
  onError: (m: string) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [p, setP] = useState<number | null>(null);
  const send = (f: File | undefined) => {
    if (!f) return;
    if (!ACCEPT.split(',').includes(f.type)) return onError('فقط عکس، ویدیو یا PDF.');
    setP(0);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/v1/admin/support/${threadId}/attachments`);
    xhr.setRequestHeader('content-type', f.type);
    xhr.setRequestHeader('x-hopex-request', '1');
    xhr.setRequestHeader('x-file-name', encodeURIComponent(f.name.slice(0, 120)));
    xhr.upload.onprogress = (e) => e.lengthComputable && setP(e.loaded / e.total);
    xhr.onloadend = () => {
      setP(null);
      if (input.current) input.current.value = '';
      if (xhr.status >= 200 && xhr.status < 300) onDone();
      else {
        let m = 'ارسال فایل انجام نشد.';
        try {
          m =
            (JSON.parse(xhr.responseText) as { error?: { message?: string } }).error?.message ?? m;
        } catch {
          /* generic */
        }
        onError(m);
      }
    };
    xhr.send(f);
  };
  return (
    <>
      <input
        ref={input}
        type="file"
        accept={ACCEPT}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => send(e.target.files?.[0])}
      />
      <button
        type="button"
        disabled={p !== null}
        onClick={() => input.current?.click()}
        className="inline-flex h-10 cursor-pointer items-center gap-1.5 rounded-md border border-line bg-surface-1 px-3 text-[13px] font-semibold text-fg-muted hover:text-fg disabled:opacity-60"
        data-agent-attach
      >
        {p !== null ? (
          <>
            <Loader2 aria-hidden className="size-4 animate-spin" />
            {Math.round(p * 100)}%
          </>
        ) : (
          <>
            <Paperclip aria-hidden className="size-4" />
            پیوست فایل
          </>
        )}
      </button>
    </>
  );
}
